import RatingStars from '@/storybook/components/RatingStarts/RatingStars';
import Text from '@/storybook/components/Text/Text';
import CrossIcon from '@/storybook/icons/CrossIcon';
import { translateDynamicKey } from '@/types/translation';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './DropdownRatingPanel.module.css';

export type DropdownRatingPanelProps<T = number> = {
  'label'?: ReactNode;
  'onClick'?: (value: T) => void;
  'onClose'?: VoidFunction;
  'data-testid'?: string;
};

function isDisplayPrefix(index: number): boolean {
  return index > 0 && index < 5;
}

export default function DropdownRatingPanel<T>({
  label,
  onClick,
  onClose,
  'data-testid': dataTestId = 'dropdownRatingPanel',
}: DropdownRatingPanelProps<T>) {
  const { t } = useTranslation();

  return (
    <div className={styles.dropdownRatingPanelContainer} data-testid={dataTestId}>
      {label && (
        <section className={styles.labelContainer}>
          <Text as="div" className={styles.label} data-testid="dropdownRatingPanelLabel">
            <span>{t('components.levelSelectDropdown.levelTopicPrefix')}</span>
            <span className={styles.optionName}>' {label} '</span>
          </Text>
          <CrossIcon className={styles.closeIcon} onClick={onClose} data-testid="dropdownRatingPanelCloseIcon" />
        </section>
      )}
      <ul className={styles.list} data-testid="dropdownRatingPanelList">
        {Array.from({ length: 6 }, (_, listItemIndex: number) => (
          <li
            className={styles.listItem}
            key={`ratingPanel-${label}-${listItemIndex}`}
            onClick={() => onClick?.(listItemIndex as T)}
            data-testid={`ratingPanel-${label}-${listItemIndex}`}
          >
            <Text as="span">
              {isDisplayPrefix(listItemIndex) && (
                <span className={styles.prefix}>{t(`components.levelSelectDropdown.levelLabelPrefix`)}</span>
              )}
              <span>{translateDynamicKey(t, `components.levelSelectDropdown.levels.${listItemIndex}`)}</span>
            </Text>
            {listItemIndex > 0 && <RatingStars rate={listItemIndex} data-testid={`ratingStars-${listItemIndex}`} />}
          </li>
        ))}
      </ul>
    </div>
  );
}
